import { useState, useEffect, useRef } from "react";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Gear, Copy, WebhooksLogo, UserPlus, EnvelopeSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function AdminSettings() {
  const [postbackInfo, setPostbackInfo] = useState(null);
  const [inmobiPostbackInfo, setInmobiPostbackInfo] = useState(null);
  const [tokenStatus, setTokenStatus] = useState(null);
  const [tokenInput, setTokenInput] = useState("");
  const [tokenExpiry, setTokenExpiry] = useState("");
  const [savingToken, setSavingToken] = useState(false);
  const [testingToken, setTestingToken] = useState(false);
  const [logs, setLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  // Onboarding template — auto-clone a chosen pub's setup to newly-approved pubs
  const [onboarding, setOnboarding] = useState(null);
  const [publishers, setPublishers] = useState([]);
  const [savingOnboarding, setSavingOnboarding] = useState(false);
  const [briefingEmails, setBriefingEmails] = useState("");
  const [briefingMeta, setBriefingMeta] = useState(null);
  const [savingBriefing, setSavingBriefing] = useState(false);
  const [sendingBriefing, setSendingBriefing] = useState(false);
  const didFetch = useRef(false);

  const loadTokenStatus = async () => {
    try {
      const { data } = await API.get("/admin/inmobi/reporting-token");
      setTokenStatus(data);
    } catch {}
  };

  useEffect(() => {
    if (didFetch.current) return;
    didFetch.current = true;
    (async () => {
      try {
        const { data } = await API.get("/admin/postback/info");
        setPostbackInfo(data);
      } catch {}
      try {
        const { data } = await API.get("/admin/inmobi/postback-info");
        setInmobiPostbackInfo(data);
      } catch {}
      await loadTokenStatus();
      try {
        const [{ data: ob }, { data: pubs }] = await Promise.all([
          API.get("/admin/settings/onboarding"),
          API.get("/admin/publishers"),
        ]);
        setOnboarding(ob);
        setPublishers(pubs || []);
      } catch {}
      try {
        const { data } = await API.get("/admin/daily-ops/briefing/recipients");
        setBriefingEmails((data.emails || []).join("\n"));
        setBriefingMeta(data);
      } catch {}
    })();
  }, []);

  const saveToken = async () => {
    if (!tokenInput.trim()) {
      toast.error("Paste the MediaMax access token first");
      return;
    }
    setSavingToken(true);
    try {
      const payload = { access_token: tokenInput.trim() };
      if (tokenExpiry) payload.expires_at = tokenExpiry;
      const { data } = await API.put("/admin/inmobi/reporting-token", payload);
      setTokenStatus(data);
      setTokenInput("");
      toast.success("Reporting token saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save token");
    } finally {
      setSavingToken(false);
    }
  };

  const testToken = async () => {
    setTestingToken(true);
    try {
      const { data } = await API.post("/admin/inmobi/reporting-token/test");
      if (data?.ok) {
        toast.success("MediaMax connection OK");
        await loadTokenStatus();
      } else {
        toast.error(data?.error || "Connection test failed");
        await loadTokenStatus();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Connection test failed");
      await loadTokenStatus();
    } finally {
      setTestingToken(false);
    }
  };

  const clearToken = async () => {
    if (!window.confirm("Remove the Shopnomix Kevin reporting token?")) return;
    try {
      await API.delete("/admin/inmobi/reporting-token");
      setTokenStatus({ configured: false });
      toast.success("Token cleared");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to clear token");
    }
  };

  const saveOnboarding = async (patch) => {
    setSavingOnboarding(true);
    try {
      const next = { ...onboarding, ...patch };
      await API.put("/admin/settings/onboarding", {
        template_publisher_id: next.template_publisher_id || null,
        auto_clone_on_approve: !!next.auto_clone_on_approve,
      });
      const { data } = await API.get("/admin/settings/onboarding");
      setOnboarding(data);
      toast.success("Onboarding settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSavingOnboarding(false);
    }
  };

  const saveBriefingEmails = async () => {
    setSavingBriefing(true);
    try {
      const { data } = await API.put("/admin/daily-ops/briefing/recipients", {
        emails: briefingEmails,
      });
      setBriefingEmails((data.emails || []).join("\n"));
      setBriefingMeta((m) => ({ ...(m || {}), emails: data.emails }));
      toast.success(
        data.emails?.length
          ? `Saved ${data.emails.length} recipient${data.emails.length === 1 ? "" : "s"}`
          : "Recipients cleared — no briefing email will be sent",
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save recipients");
    } finally {
      setSavingBriefing(false);
    }
  };

  const sendBriefingNow = async () => {
    setSendingBriefing(true);
    try {
      const { data } = await API.post("/admin/daily-ops/briefing/send");
      if (data?.reason === "no_recipients") {
        toast.error("Add at least one email and Save first");
      } else if (data?.failed?.length && !data?.sent?.length) {
        toast.error(data.failed[0]?.error || "Send failed");
      } else {
        toast.success(
          `Briefing sent to ${(data.sent || []).join(", ") || "recipients"}`,
        );
        const { data: rec } = await API.get("/admin/daily-ops/briefing/recipients");
        setBriefingMeta(rec);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send briefing");
    } finally {
      setSendingBriefing(false);
    }
  };

  const fetchLogs = async () => {
    setLoadingLogs(true);
    try {
      const { data } = await API.get("/admin/postback/logs");
      setLogs(data);
    } catch {}
    setLoadingLogs(false);
  };

  const copyUrl = (url) => {
    navigator.clipboard.writeText(url);
    toast.success("Postback URL copied!");
  };

  return (
    <div className="p-8" data-testid="admin-settings-page">
      <div className="mb-6">
        <p className="text-[11px] font-bold tracking-[0.3em] uppercase text-slate-400 mb-2">
          Platform / Configuration
        </p>
        <h1 className="font-heading text-3xl font-bold text-slate-900 tracking-tight leading-none">Settings</h1>
        <p className="text-sm text-slate-500 mt-2">Manage API connections, postbacks, and platform settings</p>
      </div>

      <div className="max-w-4xl space-y-6">
        {/* Daily 10:00 IST boss briefing */}
        <div className="border border-slate-200 rounded-md p-5" data-testid="daily-briefing-settings-card">
          <div className="flex items-center gap-2 mb-1">
            <EnvelopeSimple size={18} className="text-[#002FA7]" />
            <h3 className="font-heading text-sm font-semibold text-slate-900">
              Daily network briefing (10:00 AM IST)
            </h3>
          </div>
          <p className="text-xs text-slate-500 mb-4">
            Every morning the system checks whether clicks were sent and reports were synced for all networks,
            then emails this update. Add your boss (and anyone else) here — one email per line.
          </p>
          <textarea
            className="w-full text-xs border border-slate-200 rounded px-3 py-2 font-mono min-h-[88px] mb-3"
            value={briefingEmails}
            onChange={(e) => setBriefingEmails(e.target.value)}
            placeholder={"boss@company.com\nyou@theredclub.com"}
            data-testid="daily-briefing-emails"
          />
          <div className="flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              className="bg-[#002FA7] hover:bg-[#002089] text-white"
              disabled={savingBriefing}
              onClick={saveBriefingEmails}
              data-testid="daily-briefing-save"
            >
              {savingBriefing ? "Saving…" : "Save recipients"}
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={sendingBriefing}
              onClick={sendBriefingNow}
              data-testid="daily-briefing-send-now"
            >
              {sendingBriefing ? "Sending…" : "Send today's briefing now"}
            </Button>
          </div>
          <p className="text-[11px] text-slate-400 mt-3">
            {briefingMeta?.schedule || "Every day at 10:00 AM IST"}
            {briefingMeta?.last_sent
              ? ` · Last sent ${new Date(briefingMeta.last_sent).toLocaleString()}`
              : " · Not sent yet"}
            {briefingMeta?.last_overall_status
              ? ` · Last status ${String(briefingMeta.last_overall_status).toUpperCase()}`
              : ""}
          </p>
        </div>

        {/* Onboarding template — new-pub auto-clone */}
        <div className="border border-slate-200 rounded-md p-5" data-testid="onboarding-settings-card">
          <div className="flex items-center gap-2 mb-1">
            <UserPlus size={18} className="text-[#002FA7]" />
            <h3 className="font-heading text-sm font-semibold text-slate-900">New-publisher Onboarding</h3>
          </div>
          <p className="text-xs text-slate-500 mb-4">
            When you approve a new publisher, auto-copy a template publisher's full brand setup (rates, splits, payout modes intact) so they go from "0 brands" to "ready to push traffic" without any extra clicks.
          </p>
          {!onboarding && (
            <p className="text-xs text-slate-400">Loading…</p>
          )}
          {onboarding && (
            <div className="space-y-3">
              <div className="flex items-center justify-between py-2 border-b border-slate-100">
                <div>
                  <span className="text-xs text-slate-700 font-medium">Template publisher</span>
                  {onboarding.template_publisher_id && (
                    <span className="ml-2 text-xs text-slate-400 font-mono">
                      ({onboarding.template_assignment_count} brands assigned)
                    </span>
                  )}
                </div>
                <select
                  className="text-xs border border-slate-200 rounded px-2 py-1.5 min-w-[260px] bg-white"
                  value={onboarding.template_publisher_id || ""}
                  onChange={e => saveOnboarding({ template_publisher_id: e.target.value || null })}
                  disabled={savingOnboarding}
                  data-testid="onboarding-template-select"
                >
                  <option value="">— None —</option>
                  {publishers.map(p => (
                    <option key={p.id} value={p.id}>
                      {p.email} ({p.assigned_brands_count || 0} brands)
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-center justify-between py-2">
                <div>
                  <span className="text-xs text-slate-700 font-medium">Auto-clone on approval</span>
                  <p className="text-[11px] text-slate-400 mt-0.5">
                    {onboarding.auto_clone_on_approve
                      ? "ON — newly-approved pubs will inherit the template's full setup automatically."
                      : "OFF — pubs are approved with 0 brands. Use \"Replicate to other pubs\" later to copy."}
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={onboarding.auto_clone_on_approve}
                    onChange={e => saveOnboarding({ auto_clone_on_approve: e.target.checked })}
                    disabled={savingOnboarding || !onboarding.template_publisher_id}
                    data-testid="onboarding-auto-clone-toggle"
                  />
                  <div className="w-9 h-5 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-[#002FA7] peer-disabled:opacity-50"></div>
                </label>
              </div>
              {onboarding.auto_clone_on_approve && !onboarding.template_publisher_id && (
                <p className="text-[11px] text-amber-600">Pick a template publisher first to enable auto-clone.</p>
              )}
            </div>
          )}
        </div>

        {/* Impact API */}
        <div className="border border-slate-200 rounded-md p-5">
          <div className="flex items-center gap-2 mb-4">
            <Gear size={18} className="text-slate-500" />
            <h3 className="font-heading text-sm font-semibold text-slate-900">Impact.com API</h3>
          </div>
          <div className="space-y-3">
            <div className="flex justify-between items-center py-2 border-b border-slate-100">
              <span className="text-xs text-slate-500">Connection Status</span>
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-emerald-50 text-emerald-700">Connected</span>
            </div>
            <div className="flex justify-between items-center py-2">
              <span className="text-xs text-slate-500">Network</span>
              <span className="text-xs font-medium text-slate-900">Impact.com (Mediapartners)</span>
            </div>
          </div>
        </div>

        {/* Postback URL for Impact */}
        <div className="border border-slate-200 rounded-md p-5">
          <div className="flex items-center gap-2 mb-4">
            <WebhooksLogo size={18} className="text-[#002FA7]" />
            <h3 className="font-heading text-sm font-semibold text-slate-900">Inbound Postback URL (Impact → Engagino)</h3>
          </div>
          <p className="text-xs text-slate-500 mb-3">Configure this URL in Impact.com → Settings → Postbacks to receive real-time conversion data.</p>
          {postbackInfo && (
            <div className="space-y-3">
              <div className="bg-slate-50 border border-slate-200 rounded px-3 py-2.5 font-mono text-xs text-slate-700 break-all leading-relaxed">
                {postbackInfo.postback_url}
              </div>
              <Button variant="outline" size="sm" onClick={() => copyUrl(postbackInfo.postback_url)} data-testid="copy-postback-url-btn">
                <Copy size={14} className="mr-1.5" /> Copy Postback URL
              </Button>
            </div>
          )}
        </div>

        {/* Postback URL for InMobi (displayed as Shopnomix Kevin) */}
        <div className="border border-fuchsia-200 rounded-md p-5" data-testid="inmobi-postback-card">
          <div className="flex items-center gap-2 mb-4">
            <WebhooksLogo size={18} className="text-fuchsia-600" />
            <h3 className="font-heading text-sm font-semibold text-slate-900">Inbound Postback URL (Shopnomix Kevin → Engagino)</h3>
            {inmobiPostbackInfo?.secret_configured && (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-emerald-50 text-emerald-700">
                Secret configured
              </span>
            )}
          </div>
          <p className="text-xs text-slate-500 mb-3">
            Share this S2S URL with the InMobi integrations team for non-Amazon offers.
            Amazon CPC storefronts reconcile via the MediaMax reporting token below.
            Attribution: <code className="bg-slate-100 px-1 rounded text-[10px]">subid</code> = our click id,
            <code className="bg-slate-100 px-1 rounded text-[10px] ml-1">subid2</code> = publisher subId1.
          </p>
          {inmobiPostbackInfo ? (
            <div className="space-y-3">
              <div
                className="bg-fuchsia-50/50 border border-fuchsia-100 rounded px-3 py-2.5 font-mono text-xs text-slate-700 break-all leading-relaxed"
                data-testid="inmobi-postback-url"
              >
                {inmobiPostbackInfo.postback_url}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => copyUrl(inmobiPostbackInfo.postback_url)}
                data-testid="copy-inmobi-postback-url-btn"
              >
                <Copy size={14} className="mr-1.5" /> Copy Shopnomix Kevin Postback URL
              </Button>
              {!inmobiPostbackInfo.secret_configured && (
                <p className="text-[11px] text-amber-600">
                  Set <code className="bg-amber-50 px-1 rounded">INMOBI_POSTBACK_SECRET</code> in backend/.env before sharing with InMobi in production.
                </p>
              )}
            </div>
          ) : (
            <p className="text-xs text-slate-400">Loading Shopnomix Kevin postback URL…</p>
          )}
        </div>

        {/* Shopnomix Kevin MediaMax reporting token */}
        <div className="border border-fuchsia-200 rounded-md p-5" data-testid="inmobi-reporting-token-card">
          <div className="flex items-center gap-2 mb-4">
            <Gear size={18} className="text-fuchsia-600" />
            <h3 className="font-heading text-sm font-semibold text-slate-900">
              Shopnomix Kevin Reporting Token (MediaMax)
            </h3>
            {tokenStatus?.configured && !tokenStatus?.expired && (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-emerald-50 text-emerald-700">
                Configured
              </span>
            )}
            {tokenStatus?.expired && (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-amber-50 text-amber-700">
                Expired
              </span>
            )}
            {!tokenStatus?.configured && !tokenStatus?.expired && tokenStatus && (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-600">
                Missing
              </span>
            )}
          </div>
          <p className="text-xs text-slate-500 mb-4">
            MediaMax cookie token for Amazon CPC daily reporting. Expires about every 90 days —
            paste a renewed token here (not in <code className="bg-slate-100 px-1 rounded">.env</code>).
            Never shown again after save; only the last 4 characters are displayed.
          </p>
          {tokenStatus && (
            <div className="space-y-2 text-xs mb-4">
              <div className="flex justify-between py-1.5 border-b border-slate-100">
                <span className="text-slate-500">Token</span>
                <span className="font-mono text-slate-800">
                  {tokenStatus.token_suffix ? `••••${tokenStatus.token_suffix}` : "—"}
                </span>
              </div>
              <div className="flex justify-between py-1.5 border-b border-slate-100">
                <span className="text-slate-500">Expires</span>
                <span className="text-slate-800">
                  {tokenStatus.expires_at
                    ? `${String(tokenStatus.expires_at).slice(0, 10)}${
                        tokenStatus.days_left != null ? ` (${tokenStatus.days_left}d left)` : ""
                      }`
                    : "—"}
                </span>
              </div>
              <div className="flex justify-between py-1.5 border-b border-slate-100">
                <span className="text-slate-500">Last successful pull</span>
                <span className="text-slate-800">
                  {tokenStatus.last_ok_at ? new Date(tokenStatus.last_ok_at).toLocaleString() : "—"}
                </span>
              </div>
              {tokenStatus.last_error && (
                <p className="text-[11px] text-red-600" data-testid="inmobi-token-last-error">
                  Last error: {tokenStatus.last_error}
                </p>
              )}
            </div>
          )}
          <div className="space-y-3">
            <div>
              <label className="text-[11px] font-medium text-slate-600 block mb-1">New access token</label>
              <input
                type="password"
                className="w-full text-xs border border-slate-200 rounded px-2 py-1.5 font-mono"
                value={tokenInput}
                onChange={(e) => setTokenInput(e.target.value)}
                placeholder="Paste MediaMax access_token"
                autoComplete="off"
                data-testid="inmobi-token-input"
              />
            </div>
            <div>
              <label className="text-[11px] font-medium text-slate-600 block mb-1">
                Expiry (optional — defaults to +90 days)
              </label>
              <input
                type="date"
                className="text-xs border border-slate-200 rounded px-2 py-1.5"
                value={tokenExpiry}
                onChange={(e) => setTokenExpiry(e.target.value)}
                data-testid="inmobi-token-expiry"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                className="bg-[#002FA7] hover:bg-[#002089] text-white"
                disabled={savingToken || !tokenInput.trim()}
                onClick={saveToken}
                data-testid="inmobi-token-save"
              >
                {savingToken ? "Saving…" : "Save token"}
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={testingToken || !tokenStatus?.token_suffix}
                onClick={testToken}
                data-testid="inmobi-token-test"
              >
                {testingToken ? "Testing…" : "Test connection"}
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={savingToken || !tokenStatus?.token_suffix}
                onClick={clearToken}
                data-testid="inmobi-token-clear"
              >
                Clear
              </Button>
            </div>
          </div>
        </div>

        {/* Postback Logs */}
        <div className="border border-slate-200 rounded-md p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-sm font-semibold text-slate-900">Postback Logs</h3>
            <Button variant="outline" size="sm" onClick={fetchLogs} data-testid="load-postback-logs-btn">
              {loadingLogs ? "Loading..." : "Load Logs"}
            </Button>
          </div>
          {logs.length > 0 ? (
            <div className="max-h-[300px] overflow-auto">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-slate-50">
                  <tr className="border-b border-slate-200">
                    <th className="text-left px-3 py-2 text-xs uppercase tracking-wider text-slate-500 font-bold">Direction</th>
                    <th className="text-left px-3 py-2 text-xs uppercase tracking-wider text-slate-500 font-bold">Action ID</th>
                    <th className="text-left px-3 py-2 text-xs uppercase tracking-wider text-slate-500 font-bold">SubID1</th>
                    <th className="text-left px-3 py-2 text-xs uppercase tracking-wider text-slate-500 font-bold">Status</th>
                    <th className="text-left px-3 py-2 text-xs uppercase tracking-wider text-slate-500 font-bold">Time</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((l, i) => (
                    <tr key={i} className="border-b border-slate-100">
                      <td className="px-3 py-2">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                          l.direction === "inbound" ? "bg-blue-50 text-blue-700" : "bg-purple-50 text-purple-700"
                        }`}>{l.direction}</span>
                      </td>
                      <td className="px-3 py-2 font-mono text-slate-600">{l.action_id || "-"}</td>
                      <td className="px-3 py-2 font-mono font-bold text-[#002FA7]">{l.sub_id1 || "-"}</td>
                      <td className="px-3 py-2">{l.response_status || l.error || "received"}</td>
                      <td className="px-3 py-2 text-slate-500">{l.received_at || l.sent_at || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-xs text-slate-400">No logs yet. Click "Load Logs" after a postback fires.</p>
          )}
        </div>

        {/* Platform Info */}
        <div className="border border-slate-200 rounded-md p-5">
          <h3 className="font-heading text-sm font-semibold text-slate-900 mb-4">Platform Info</h3>
          <div className="space-y-2 text-xs">
            <div className="flex justify-between py-1.5 border-b border-slate-100">
              <span className="text-slate-500">Version</span>
              <span className="text-slate-900 font-mono">1.0.0</span>
            </div>
            <div className="flex justify-between py-1.5 border-b border-slate-100">
              <span className="text-slate-500">Networks Supported</span>
              <span className="text-slate-900">Impact.com</span>
            </div>
            <div className="flex justify-between py-1.5">
              <span className="text-slate-500">Next Network</span>
              <span className="text-slate-400">Partnerize (Coming Soon)</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
